import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Cancel,
  Payment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionStatus, createSubscription, changeSubscription, cancelSubscription, uncancelSubscription, cancelScheduledChange, getAvailablePlans, previewSubscriptionChange, createCustomerPortalSession, restoreSubscription, getPricingPage } from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import BaseDialog from '../dialogs/BaseDialog';

interface Plan {
  id: number;
  name: string;
  price_monthly?: number;
  price_yearly?: number;
}

interface SubscriptionStatus {
  plan_type: number;
  billing_period: string;
  status: string;
  current_period_end: string | null;
  is_active: boolean;
  scheduled_plan_type?: number | null;
  scheduled_billing_period?: string | null;
}

interface ProrationDetail {
  description: string;
  amount: number;
}

interface ChangePreview {
  current_plan: string;
  new_plan: string;
  current_billing_period: string;
  new_billing_period: string;
  is_upgrade: boolean;
  immediate_amount: number;
  next_billing_date: string | null;
  proration_details: ProrationDetail[];
}

const SubscriptionSettings: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUser } = useUser();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showUncancelDialog, setShowUncancelDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [preview, setPreview] = useState<ChangePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [restoreLoading, setRestoreLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansResponse, statusResponse, pricingResponse] = await Promise.all([
        getAvailablePlans(),
        getSubscriptionStatus(),
        getPricingPage(),
      ]);
      // 価格情報をマージ
      const pricingPlans = pricingResponse.data?.plans || [];
      const merged = plansResponse.data.map((p: Plan) => {
        const match = pricingPlans.find((pp: any) => pp.plan_type === p.id);
        return {
          ...p,
          price_monthly: match?.price_monthly,
          price_yearly: match?.price_yearly,
        };
      });
      setPlans(merged);
      setSubscription(statusResponse.data);
      if (statusResponse.data.billing_period) {
        setBillingPeriod(statusResponse.data.billing_period);
      }
    } catch (err: any) {
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan: Plan, period: 'monthly' | 'yearly') => {
    setSelectedPlan(plan);
    setBillingPeriod(period);
    setPreview(null);
    setShowUpgradeDialog(true);

    // アクティブなサブスクがある場合のみプレビュー取得
    if (subscription?.is_active && subscription.status !== 'canceled') {
      try {
        setPreviewLoading(true);
        const res = await previewSubscriptionChange({ plan_type: plan.id, billing_period: period });
        setPreview(res.data);
      } catch {
        // プレビュー取得失敗しても変更は可能にする
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!selectedPlan) return;

    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);

      // アクティブなサブスクがあればプラン変更、なければ新規作成
      if (subscription?.is_active && subscription.status !== 'canceled') {
        const response = await changeSubscription({ plan_type: selectedPlan.id, billing_period: billingPeriod });
        setSuccess(response.data.message || '変更を受け付けました');
        fetchData();
        refreshUser();
      } else {
        const response = await createSubscription({ plan_type: selectedPlan.id, billing_period: billingPeriod });
        // Stripeのチェックアウトページにリダイレクト
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'サブスクリプションの作成に失敗しました');
    } finally {
      setActionLoading(false);
      setShowUpgradeDialog(false);
      setSelectedPlan(null);
    }
  };

  const handleUncancelSubscription = async () => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);
      await uncancelSubscription();
      setSuccess('キャンセルを取り消しました');
      setShowUncancelDialog(false);
      fetchData();
      refreshUser();
    } catch (err: any) {
      setError(err.response?.data?.error || 'キャンセル取り消しに失敗しました');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);

      await cancelSubscription();
      setSuccess('サブスクリプションをキャンセルしました');
      setShowCancelDialog(false);

      // データを再取得
      fetchData();
      refreshUser();
    } catch (err: any) {
      setError(err.response?.data?.error || 'サブスクリプションのキャンセルに失敗しました');
    } finally {
      setActionLoading(false);
    }
  };

  const getCurrentPlan = () => {
    // 期限切れ/未加入は Free 扱い（plan_type に最後の購入プランが残っていても無視）
    if (!subscription || !subscription.is_active) return undefined;
    return plans.find(p => p.id === subscription.plan_type);
  };

  const getPlanStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'canceled':
        return 'error';
      case 'past_due':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPlanStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'アクティブ';
      case 'canceled':
        return 'キャンセル済み';
      case 'past_due':
        return '支払い遅延';
      case 'inactive':
        return '非アクティブ';
      default:
        return status;
    }
  };

  const isSubscriptionExpired = () => {
    if (!subscription || !subscription.current_period_end) return false;
    return new Date() > new Date(subscription.current_period_end);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const currentPlan = getCurrentPlan();
  const isCrowdfunding = subscription?.plan_type === 9;
  const scheduledPlanName = subscription?.scheduled_plan_type != null
    ? (plans.find(p => p.id === subscription.scheduled_plan_type)?.name || '')
    : '';
  const scheduledPeriodLabel = subscription?.scheduled_billing_period === 'yearly'
    ? '年額'
    : subscription?.scheduled_billing_period === 'monthly'
    ? '月額'
    : '';

  return (
    <Box>
      {/* 現在のプラン */}
      <Box
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: subscription?.is_active ? 'primary.main' : 'divider',
          bgcolor: subscription?.is_active ? 'rgba(217,255,0,0.03)' : 'transparent',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {isCrowdfunding ? '特典プラン' : (currentPlan?.name || 'Free')}
            </Typography>
            {subscription && subscription.is_active && subscription.status !== 'inactive' && !isCrowdfunding && (
              <Chip
                label={getPlanStatusText(subscription.status)}
                color={getPlanStatusColor(subscription.status) as any}
                size="small"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            )}
            {isCrowdfunding && (
              isSubscriptionExpired() ? (
                <Chip
                  label="期限切れ"
                  size="small"
                  color="warning"
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                />
              ) : (
                <Chip
                  label="特典適用中"
                  size="small"
                  sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'rgba(217,255,0,0.15)', color: 'primary.main', fontWeight: 700 }}
                />
              )
            )}
          </Box>
        </Box>

        {subscription?.is_active && subscription?.current_period_end && !isCrowdfunding && (
          <Typography variant="caption" color="text.secondary">
            {subscription.status === 'canceled' ? '有効期限' : '次回更新日'}:
            {' '}{new Date(subscription.current_period_end).toLocaleDateString('ja-JP')}
          </Typography>
        )}

        {subscription?.status === 'canceled' && !isSubscriptionExpired() && !isCrowdfunding && (
          <Typography variant="caption" display="block" color="warning.main" sx={{ mt: 0.5 }}>
            キャンセル済み — 期間終了までご利用いただけます
          </Typography>
        )}

        {subscription?.scheduled_plan_type != null && !isCrowdfunding && (
          <Box mt={0.75} display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="caption" color="info.main">
              次回更新時に {scheduledPlanName}（{scheduledPeriodLabel}）に切り替え予定
            </Typography>
            <Button
              size="small"
              sx={{ fontSize: '0.65rem', p: 0, minWidth: 0, color: 'text.secondary', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
              onClick={async () => {
                try {
                  setError(null);
                  setSuccess(null);
                  await cancelScheduledChange();
                  setSuccess('切り替え予約を取り消しました');
                  fetchData();
                  refreshUser();
                } catch (err: any) {
                  setError(err.response?.data?.error || '取消に失敗しました');
                }
              }}
            >
              予約を取り消す
            </Button>
          </Box>
        )}

        {isCrowdfunding && (
          <>
            {subscription?.current_period_end && (
              <Typography variant="caption" color="text.secondary" display="block">
                {isSubscriptionExpired()
                  ? `有効期限 ${new Date(subscription.current_period_end).toLocaleDateString('ja-JP')} を経過しました`
                  : `有効期限: ${new Date(subscription.current_period_end).toLocaleDateString('ja-JP')}`}
              </Typography>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: isSubscriptionExpired() ? 'warning.main' : 'text.secondary' }}>
              {isSubscriptionExpired()
                ? '特典期間は終了しています。システム処理の都合で一時的にPRO機能をご利用いただけますが、処理完了後は無料プランに戻ります。'
                : 'クラウドファンディング支援特典としてプラン機能を提供中です。'}
            </Typography>
          </>
        )}

        {!isCrowdfunding && (
          <Box mt={1.5} display="flex" gap={2}>
            {/* 過去に課金歴があれば（解約済・期限切れ含む）Customer Portal は開ける */}
            {subscription && subscription.status !== 'inactive' && (
              <Button
                size="small"
                sx={{ fontSize: '0.7rem', p: 0, minWidth: 0, color: 'text.secondary', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                onClick={async () => {
                  try {
                    const res = await createCustomerPortalSession();
                    window.location.href = res.data.url;
                  } catch {
                    setError('ポータルを開けませんでした');
                  }
                }}
              >
                支払い履歴・カード管理
              </Button>
            )}
            <Button
              size="small"
              disabled={restoreLoading}
              startIcon={restoreLoading ? <CircularProgress size={12} sx={{ color: 'text.secondary' }} /> : undefined}
              sx={{ fontSize: '0.7rem', p: 0, minWidth: 0, color: 'text.secondary', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
              onClick={async () => {
                try {
                  setRestoreLoading(true);
                  setError(null);
                  setSuccess(null);
                  const res = await restoreSubscription();
                  if (res.data.restored) {
                    setSuccess(res.data.message);
                    fetchData();
                    refreshUser();
                  } else {
                    setError(res.data.message);
                  }
                } catch (err: any) {
                  setError(err.response?.data?.error || '復元に失敗しました');
                } finally {
                  setRestoreLoading(false);
                }
              }}
            >
              {restoreLoading ? '復元中...' : '購入を復元'}
            </Button>
          </Box>
        )}
      </Box>

      {/* エラー・成功メッセージ（現在のプラン直下） */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* プラン一覧（クラファン特典ユーザーにはグレーアウト） */}
      <Box sx={{ opacity: isCrowdfunding ? 0.4 : 1, pointerEvents: isCrowdfunding ? 'none' : 'auto' }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 1.5 }}>
          プラン
        </Typography>
        <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
          {plans
            .filter(plan => plan.id > 0)
            .map((plan) => {
              const isCurrent = currentPlan?.id === plan.id;
              return (
                <Box
                  key={plan.id}
                  sx={{
                    flex: 1,
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: isCurrent ? 'primary.main' : 'divider',
                    bgcolor: isCurrent ? 'rgba(217,255,0,0.03)' : 'transparent',
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {plan.name}
                    </Typography>
                    {isCurrent && (
                      <Chip
                        label="現在"
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 700,
                          bgcolor: 'rgba(217,255,0,0.15)', color: 'primary.main',
                        }}
                      />
                    )}
                  </Box>
                  <Button
                    fullWidth
                    variant={isCurrent ? 'outlined' : 'contained'}
                    disabled={
                      (isCurrent && !subscription?.is_active) ||
                      (!isCurrent && subscription?.is_active === true && (subscription?.status === 'canceled' || subscription?.scheduled_plan_type != null))
                    }
                    onClick={() => {
                      if (isCurrent) {
                        setShowManageDialog(true);
                      } else if (!subscription?.is_active || subscription?.plan_type === 0) {
                        navigate('/pricing');
                      } else {
                        handleUpgrade(plan, subscription?.billing_period === 'yearly' ? 'yearly' : 'monthly');
                      }
                    }}
                    size="small"
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1.5,
                      ...(isCurrent
                        ? { borderColor: 'divider', color: 'text.secondary' }
                        : {}),
                    }}
                  >
                    {isCurrent
                      ? 'プランを管理'
                      : (!subscription?.is_active || subscription?.plan_type === 0)
                        ? '料金プランを見る'
                        : (plan.id > (subscription?.plan_type ?? 0) ? 'アップグレード' : 'ダウングレード')}
                  </Button>
                </Box>
              );
            })}
        </Box>
      </Box>

      {/* Stripe セキュリティバッジ */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        mt={3}
        mb={3}
        py={1.5}
        px={2}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'rgba(255,255,255,0.02)',
        }}
      >
        <Box
          component="svg"
          width={20}
          height={20}
          viewBox="0 0 24 24"
          fill="none"
          sx={{ color: 'success.main', flexShrink: 0 }}
        >
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" fill="currentColor" opacity="0.2"/>
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M10 15.5l-3.5-3.5 1.41-1.41L10 12.67l5.59-5.59L17 8.5l-7 7z" fill="currentColor"/>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', letterSpacing: 0.3 }}>
          決済情報はPULSEのサーバーを経由せず、PCI DSS（国際的なクレジットカードセキュリティ基準）準拠の決済サービス<Box component="span" sx={{ color: 'text.primary' }}>Stripe</Box>に直接送信され、安全に処理されます。本サービスではユーザーのカード情報を一切取得・保持しません。
        </Typography>
      </Box>

      {/* プラン選択ダイアログ */}
      {(() => {
        const isChange = subscription?.is_active && subscription.status !== 'canceled';
        const isPeriodUp = !!(
          isChange && selectedPlan
          && selectedPlan.id === subscription?.plan_type
          && subscription?.billing_period === 'monthly'
          && billingPeriod === 'yearly'
        );
        const isPeriodDown = !!(
          isChange && selectedPlan
          && selectedPlan.id === subscription?.plan_type
          && subscription?.billing_period === 'yearly'
          && billingPeriod === 'monthly'
        );
        const isUpgrade = !!(
          isChange && selectedPlan
          && (selectedPlan.id > (subscription?.plan_type ?? 0) || isPeriodUp)
        );
        const isDowngrade = !!(
          isChange && selectedPlan
          && (selectedPlan.id < (subscription?.plan_type ?? 0) || isPeriodDown)
        );
        const dialogTitle = !isChange
          ? 'プランの選択'
          : isUpgrade
          ? 'プランをアップグレード'
          : isDowngrade
          ? 'プランをダウングレード'
          : 'プランの変更';
        const confirmLabel = !isChange
          ? 'プランを選択'
          : isUpgrade
          ? 'アップグレード'
          : isDowngrade
          ? 'ダウングレード'
          : 'プランを変更';
        return (
          <BaseDialog
            open={showUpgradeDialog}
            onClose={() => setShowUpgradeDialog(false)}
            title={dialogTitle}
            maxWidth="sm"
            actions={
              <>
                <Button onClick={() => setShowUpgradeDialog(false)}>
                  キャンセル
                </Button>
                <Button
                  onClick={handleConfirmUpgrade}
                  variant="contained"
                  disabled={actionLoading || previewLoading}
                  startIcon={actionLoading ? <CircularProgress size={20} /> : <Payment />}
                >
                  {actionLoading ? '処理中...' : confirmLabel}
                </Button>
              </>
            }
          >
            {selectedPlan && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {selectedPlan.name} プラン
                </Typography>

                {!isChange && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Stripeの決済ページに移動します。
                  </Typography>
                )}

                {isChange && previewLoading && (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {isChange && preview && (
                  <Box>
                    {isUpgrade ? (
                      <Box sx={{ mt: 1 }}>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2" color="text.secondary">今回の請求額</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            ¥{preview.immediate_amount.toLocaleString()}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          日割り計算により、現在のプランの残り期間分が差し引かれます。
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        現在の期間終了後に新しい料金が適用されます。
                      </Typography>
                    )}
                    {preview.next_billing_date && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        次回請求日: {new Date(preview.next_billing_date).toLocaleDateString('ja-JP')}
                      </Typography>
                    )}
                  </Box>
                )}

                {isChange && !previewLoading && !preview && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    請求プレビューを取得できませんでした。変更を続行することは可能です。
                  </Alert>
                )}
              </Box>
            )}
          </BaseDialog>
        );
      })()}

      {/* プラン管理ダイアログ（現在プランに対する操作集） */}
      <BaseDialog
        open={showManageDialog}
        onClose={() => setShowManageDialog(false)}
        title="プランを管理"
        actions={
          <Button onClick={() => setShowManageDialog(false)}>閉じる</Button>
        }
      >
        <Box display="flex" flexDirection="column" gap={1.25}>
          {/* キャンセル取り消し */}
          {subscription?.status === 'canceled' && !isSubscriptionExpired() && (
            <Button
              variant="contained"
              onClick={() => {
                setShowManageDialog(false);
                setShowUncancelDialog(true);
              }}
            >
              キャンセルを取り消す
            </Button>
          )}

          {/* ダウングレード予約取消 */}
          {subscription?.scheduled_plan_type != null && subscription.status !== 'canceled' && (
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  setError(null);
                  setSuccess(null);
                  await cancelScheduledChange();
                  setSuccess('切り替え予約を取り消しました');
                  setShowManageDialog(false);
                  fetchData();
                  refreshUser();
                } catch (err: any) {
                  setError(err.response?.data?.error || '取消に失敗しました');
                }
              }}
            >
              切り替え予約を取り消す
            </Button>
          )}

          {/* 期間切替 (canceled・scheduled以外) */}
          {subscription?.status !== 'canceled' && subscription?.scheduled_plan_type == null && currentPlan && (
            <Button
              variant="outlined"
              onClick={() => {
                setShowManageDialog(false);
                handleUpgrade(currentPlan, subscription?.billing_period === 'monthly' ? 'yearly' : 'monthly');
              }}
            >
              {subscription?.billing_period === 'monthly' ? '年額に切り替え' : '月額に切り替え'}
            </Button>
          )}

          {/* 解約 (canceled以外) */}
          {subscription?.status !== 'canceled' && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setShowManageDialog(false);
                setShowCancelDialog(true);
              }}
            >
              プランを解約
            </Button>
          )}
        </Box>
      </BaseDialog>

      {/* キャンセル確認ダイアログ */}
      <BaseDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        title="サブスクリプションのキャンセル"
        actions={
          <>
            <Button onClick={() => setShowCancelDialog(false)}>
              キャンセル
            </Button>
            <Button
              onClick={handleCancelSubscription}
              variant="contained"
              color="error"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={20} /> : <Cancel />}
            >
              {actionLoading ? '処理中...' : 'キャンセルする'}
            </Button>
          </>
        }
      >
        <Typography variant="body1" paragraph>
          サブスクリプションをキャンセルしますか？
        </Typography>
        <Typography variant="body2" color="text.secondary">
          キャンセル後も現在の期間終了まではサービスをご利用いただけます。
        </Typography>
      </BaseDialog>

      {/* キャンセル取消確認ダイアログ */}
      <BaseDialog
        open={showUncancelDialog}
        onClose={() => setShowUncancelDialog(false)}
        title="キャンセルの取り消し"
        actions={
          <>
            <Button onClick={() => setShowUncancelDialog(false)}>
              キャンセル
            </Button>
            <Button
              onClick={handleUncancelSubscription}
              variant="contained"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={20} /> : undefined}
            >
              {actionLoading ? '処理中...' : '取り消す'}
            </Button>
          </>
        }
      >
        <Typography variant="body1" paragraph>
          キャンセル予約を取り消しますか？
        </Typography>
        <Typography variant="body2" color="text.secondary">
          サブスクリプションは通常通り自動更新されるようになります。
        </Typography>
      </BaseDialog>
    </Box>
  );
};

export default SubscriptionSettings; 